import { useMemo } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { NotificationsApiAdapter } from '../adapters/NotificationsApiAdapter';
import type { NotificationReadFilter } from '../models/Notification.types';
import { useAuthStore } from '@shared/stores/authStore';

const PER_PAGE = 20;

function isSameUserId(
  notificationUserId: number | string | undefined,
  currentUserId: number | string | undefined,
): boolean {
  if (currentUserId == null || notificationUserId == null) return false;
  return Number(notificationUserId) === Number(currentUserId);
}

/** No archivada: null, undefined o cadena vacía desde API */
function isNotArchived(archivedAt: string | null | undefined): boolean {
  return !archivedAt;
}

export const useNotifications = (readFilter: NotificationReadFilter = 'unread') => {
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new NotificationsApiAdapter(httpClient);
  const user = useAuthStore((state) => state.user);
  const userId = user?.id;

  const { data, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery({
      queryKey: ['notifications', readFilter, userId],
      queryFn: async ({ pageParam = 1 }) => {
        const filters = adapter.buildFiltersForReadFilter(readFilter);
        const response = await adapter.getNotifications({
          page: pageParam,
          per_page: PER_PAGE,
          sortColumn: 'id',
          sortDirection: 'desc',
          filters,
        });

        return {
          notifications: response.data,
          meta: response.meta,
          nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
        };
      },
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      staleTime: 2 * 60 * 1000,
      placeholderData: (previousData) => previousData,
      enabled: !!userId,
    });

  const allNotifications = useMemo(() => data?.pages.flatMap((page) => page.notifications) ?? [], [data?.pages]);

  // API puede devolver notificaciones de todo el equipo: filtrar por usuario actual.
  // user_id a veces viene como string en JSON; archived_at a veces "" en vez de null.
  const notifications = useMemo(
    () => allNotifications.filter((n) => isSameUserId(n.user_id, userId) && isNotArchived(n.archived_at)),
    [allNotifications, userId],
  );

  const total = data?.pages?.[0]?.meta.total ?? 0;

  return {
    notifications,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total,
  };
};
